import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowClockwise, Package, Plus } from 'shared/icons';
import PageHeader from '../../components/layout/PageHeader';
import Button from '../../components/ui/Button';
import ErrorCard from '../../components/ui/ErrorCard';
import Select from '../../components/ui/Select';
import AllocationWorkspace from '../../components/warehouse/allocation/AllocationWorkspace';
import { ExtractListRows } from '../../components/warehouse/payload';
import IncidentsLane from '../../components/warehouse/serviceorders/IncidentsLane';
import ServiceOrderBoard from '../../components/warehouse/serviceorders/ServiceOrderBoard';
import ServiceOrderCreateModal from '../../components/warehouse/serviceorders/ServiceOrderCreateModal';
import ServiceOrderDetailModal from '../../components/warehouse/serviceorders/ServiceOrderDetailModal';
import {
    CONSOLE_STATUS_CANCELED,
    CONSOLE_STATUS_READY,
    SERVICE_ORDER_STATUS,
} from '../../components/warehouse/serviceorders/serviceOrderModel';
import { useWarehouseLabels } from '../../components/warehouse/warehouseLabels';
import { useAuth } from '../../hooks/useAuth';
import api from '../../services/api';
import { ExtractPaging } from '../../utils/aggregation';

/**
 * /warehouse/service-orders — CONSOLE DO SUPERVISOR (M5).
 *
 * ── Uma rota, duas superfícies ──────────────────────────────────────────────
 * O quadro (kanban) e a alocação são a MESMA jornada: o Supervisor aloca carga
 * e o resultado cai como OSs liberadas no quadro que ele está olhando. Por
 * isso a alocação é uma VISÃO desta página (botão "Alocar carga" → volta) e
 * não um endereço separado no menu: dois itens de menu para o mesmo trabalho
 * fariam o Supervisor navegar entre eles para ver o efeito do que acabou de
 * fazer. O menu ganha UM item, como as outras telas do módulo.
 *
 * ── Uma consulta por COLUNA ─────────────────────────────────────────────────
 * A listagem ordena por status e depois por data decrescente, então uma única
 * consulta com teto de N devolveria, com o tempo, um quadro só de concluídas —
 * as colunas de trabalho vivo secariam em silêncio. Cada coluna pede o SEU
 * status com o seu teto: as de trabalho ativo cabem inteiras; a de concluídas
 * mostra as recentes e avisa quando há mais.
 *
 * PAUSED entra na mesma consulta lógica de IN_PROGRESS porque compartilha a
 * coluna (uma OS pausada continua atribuída — ver `serviceOrderModel.js`).
 */

// Teto por coluna. Trabalho vivo cabe inteiro no quadro (uma operação com mais
// de 100 OSs simultâneas em fila tem um problema que não é de tela); as
// terminais mostram o recente e avisam que há mais.
const ACTIVE_LIMIT = 100;
const TERMINAL_LIMIT = 30;

const BOARD_QUERIES = [
    { status: SERVICE_ORDER_STATUS.BACKLOG, limit: ACTIVE_LIMIT },
    { status: SERVICE_ORDER_STATUS.READY, limit: ACTIVE_LIMIT },
    { status: SERVICE_ORDER_STATUS.IN_PROGRESS, limit: ACTIVE_LIMIT },
    { status: SERVICE_ORDER_STATUS.PAUSED, limit: ACTIVE_LIMIT },
    { status: SERVICE_ORDER_STATUS.DONE, limit: TERMINAL_LIMIT },
];

const VIEW_BOARD = 'BOARD';
const VIEW_ALLOCATION = 'ALLOCATION';

const WarehouseServiceOrdersPage = () => {
    const { label } = useWarehouseLabels();
    const { checkPermission } = useAuth();
    const navigate = useNavigate();

    const [view, setView] = useState(VIEW_BOARD);
    const [orders, setOrders] = useState([]);
    const [truncated, setTruncated] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [includeCanceled, setIncludeCanceled] = useState(false);
    const [typeId, setTypeId] = useState('');
    const [types, setTypes] = useState([]);
    const [detailId, setDetailId] = useState(null);
    const [creating, setCreating] = useState(false);
    const [busyId, setBusyId] = useState(null);
    const [laneToken, setLaneToken] = useState(0);

    const canManage = checkPermission('wms:manage:service_orders');
    const canReadIncidents = checkPermission('wms:manage:incidents');
    const canReadLocations = checkPermission('wms:manage:locations');
    const canReadLpns = checkPermission('wms:manage:lpns');
    const canReadEquipment = checkPermission('wms:manage:equipment');

    const load = useCallback(async () => {
        setLoading(true);
        setLoadError(null);
        try {
            const queries = includeCanceled
                ? [...BOARD_QUERIES, { status: SERVICE_ORDER_STATUS.CANCELED, limit: TERMINAL_LIMIT }]
                : BOARD_QUERIES;
            const responses = await Promise.all(
                queries.map((query) =>
                    api.getWarehouseServiceOrders({
                        status: query.status,
                        limit: query.limit,
                        ...(typeId ? { type_id: typeId } : {}),
                    }),
                ),
            );
            const rows = [];
            let anyTruncated = false;
            responses.forEach((payload, index) => {
                const list = ExtractListRows(payload, ['service_orders']);
                rows.push(...list);
                const paging = ExtractPaging(payload, { total: list.length, limit: queries[index].limit, offset: 0 });
                if (paging.total > list.length) {
                    anyTruncated = true;
                }
            });
            setOrders(rows);
            setTruncated(anyTruncated);
        } catch (error) {
            setLoadError(error?.message || label('wms.common.load_error'));
            setOrders([]);
        } finally {
            setLoading(false);
        }
    }, [includeCanceled, typeId, label]);

    useEffect(() => {
        load();
    }, [load]);

    // Filtro por tipo de OS: cadastro pequeno, carregado uma vez. Falha aqui
    // não derruba o quadro — o filtro apenas não aparece.
    useEffect(() => {
        let alive = true;
        api.getWarehouseServiceOrderTypes({ active: 'true', limit: 200 })
            .then((data) => {
                if (alive) {
                    setTypes(ExtractListRows(data, ['service_order_types']));
                }
            })
            .catch(() => {
                if (alive) {
                    setTypes([]);
                }
            });
        return () => {
            alive = false;
        };
    }, []);

    const refreshAll = useCallback(() => {
        load();
        setLaneToken((token) => token + 1);
    }, [load]);

    const patchStatus = async (order, status) => {
        setBusyId(order.id);
        try {
            await api.updateWarehouseServiceOrder(order.id, { status });
            await load();
        } catch (error) {
            setLoadError(error?.message || label('wms.common.load_error'));
        } finally {
            setBusyId(null);
        }
    };

    // "Alocar carga" é PRIMÁRIO e "Nova OS" é secundário de propósito: o
    // caminho normal é o planejador montar a jornada do pallet; a criação
    // avulsa é a saída de emergência para quando ele não descreve o caso real.
    // A hierarquia visual diz qual é qual antes de o Supervisor clicar.
    const headerActions = useMemo(
        () => (
            <>
                {canManage && view === VIEW_BOARD ? (
                    <>
                        <Button variant="primary" onClick={() => setView(VIEW_ALLOCATION)}>
                            <Package size={18} className="mr-2" />
                            {label('wms.allocation.title')}
                        </Button>
                        <Button variant="secondary" onClick={() => setCreating(true)}>
                            <Plus size={18} className="mr-2" />
                            {label('wms.service_orders.new')}
                        </Button>
                    </>
                ) : null}
                <Button variant="secondary" onClick={refreshAll} loading={loading}>
                    <ArrowClockwise size={18} className="mr-2" />
                    {label('wms.common.retry')}
                </Button>
            </>
        ),
        [canManage, view, label, refreshAll, loading],
    );

    return (
        <div className="space-y-6">
            <PageHeader
                title={label('wms.service_orders.title')}
                subtitle={label('wms.service_orders.description')}
                actions={headerActions}
            />

            {view === VIEW_ALLOCATION ? (
                <AllocationWorkspace
                    label={label}
                    canReadLpns={canReadLpns}
                    canReadLocations={canReadLocations}
                    onBack={() => setView(VIEW_BOARD)}
                    onGenerated={refreshAll}
                />
            ) : loadError ? (
                <ErrorCard
                    title={label('wms.service_orders.title')}
                    message={loadError}
                    action={label('wms.common.retry')}
                    actionFn={refreshAll}
                />
            ) : (
                <>
                    <div className="flex flex-wrap items-end gap-3">
                        {types.length > 0 ? (
                            <div className="w-64">
                                <Select
                                    label={label('wms.service_order.field.type')}
                                    value={typeId}
                                    onChange={(event) => setTypeId(event.target.value)}
                                >
                                    <option value="">{label('wms.locations.area_all')}</option>
                                    {types.map((type) => (
                                        <option key={type.id} value={type.id}>
                                            {type.label || type.code}
                                        </option>
                                    ))}
                                </Select>
                            </div>
                        ) : null}

                        {/* Canceladas fora do quadro por padrão: trabalho
                            interrompido não pode se misturar com concluído sem
                            que alguém tenha pedido para vê-lo. Chip pressionável
                            (mesmo padrão do filtro da página de Incidentes) —
                            uma caixa de seleção solta escrita "Cancelada" não
                            diz se está filtrando ou marcando algo. */}
                        <button
                            type="button"
                            onClick={() => setIncludeCanceled((previous) => !previous)}
                            aria-pressed={includeCanceled}
                            className={`mb-0.5 rounded-lg border px-4 py-2 text-sm font-medium transition-colors ${
                                includeCanceled
                                    ? 'border-black bg-black text-white dark:border-white dark:bg-white dark:text-black'
                                    : 'border-mono-300 bg-white text-mono-700 hover:bg-mono-50 dark:border-mono-700 dark:bg-mono-950 dark:text-mono-300 dark:hover:bg-mono-900'
                            }`}
                        >
                            {label('wms.service_order.status.CANCELED')}
                        </button>
                    </div>

                    <ServiceOrderBoard
                        orders={orders}
                        label={label}
                        loading={loading}
                        canManage={canManage}
                        includeCanceled={includeCanceled}
                        busyId={busyId}
                        onOpen={(order) => setDetailId(order.id)}
                        onApprove={(order) => patchStatus(order, CONSOLE_STATUS_READY)}
                        onDeny={(order) => patchStatus(order, CONSOLE_STATUS_CANCELED)}
                    />

                    {truncated ? (
                        <p className="text-xs text-mono-500 dark:text-mono-400">
                            {label('wms.locations.partial_list')}
                        </p>
                    ) : null}

                    {/* O cartão da raia leva à FILA de incidentes, que é onde
                        o caso se conduz (histórico, anotações, resolução). O
                        quadro mostra que existe; a página de incidentes é
                        quem trata. */}
                    <IncidentsLane
                        label={label}
                        canReadIncidents={canReadIncidents}
                        canReadLocations={canReadLocations}
                        canReadLpns={canReadLpns}
                        refreshToken={laneToken}
                        onOpenIncident={() => navigate('/warehouse/incidents')}
                    />
                </>
            )}

            {/* Criação AVULSA: os tipos ATIVOS são os mesmos que alimentam o
                filtro acima — o cadastro é carregado uma vez por página, e não
                uma vez por superfície que precisa dele. */}
            {creating ? (
                <ServiceOrderCreateModal
                    label={label}
                    types={types}
                    canReadLocations={canReadLocations}
                    canReadEquipment={canReadEquipment}
                    onClose={() => setCreating(false)}
                    onCreated={refreshAll}
                    onUseAllocation={() => {
                        setCreating(false);
                        setView(VIEW_ALLOCATION);
                    }}
                />
            ) : null}

            {detailId ? (
                <ServiceOrderDetailModal
                    serviceOrderId={detailId}
                    label={label}
                    canManage={canManage}
                    onClose={() => setDetailId(null)}
                    onChanged={refreshAll}
                />
            ) : null}
        </div>
    );
};

export default WarehouseServiceOrdersPage;
